import { isObject } from './helpers/is.js'
import { toIntegerWithoutRounding, toString } from './helpers/utils.js'
import { parseDurationString } from './helpers/parsers.js'
import { rejectDuration } from './helpers/regulate.js'
import { balanceDuration, totalDurationNanoseconds } from './helpers/balance.js'
import { THOUSAND, ZERO, NEGATIVE_ONE } from './helpers/constants.js'
import { DurationFormat, Locale } from '@neumatter/intl'
import {
  addDuration,
  addZonedDateTime,
  adjustRoundedDurationDays,
  differenceISODate,
  getDefaultLargestUnit,
  getLargestUnit,
  roundDuration
} from './helpers/math.js'
import DateTime from './DateTime.js'

const SYMBOL_IS_DURATION = Symbol.for('neumatter.Duration.isDuration')

export default class Duration {
  static isDuration (item) {
    return (
      typeof item === 'object' &&
      typeof item[SYMBOL_IS_DURATION] === 'boolean'
    )
  }

  /**
   *
   * @param {Duration} a
   * @param {Duration} b
   */
  static compare (a, b) {
    a = Duration.isDuration(a) ? a : Duration.from(a)
    b = Duration.isDuration(b) ? b : Duration.from(b)

    if (a.years !== b.years) {
      return a.years < b.years ? -1 : 1
    }

    if (a.months !== b.months) {
      return a.months < b.months ? -1 : 1
    }

    if (a.weeks !== b.weeks) {
      return a.weeks < b.weeks ? -1 : 1
    }

    if (a.days !== b.days) {
      return a.days < b.days ? -1 : 1
    }

    if (a.hours !== b.hours) {
      return a.hours < b.hours ? -1 : 1
    }

    if (a.minutes !== b.minutes) {
      return a.minutes < b.minutes ? -1 : 1
    }

    if (a.seconds !== b.seconds) {
      return a.seconds < b.seconds ? -1 : 1
    }

    if (a.milliseconds !== b.milliseconds) {
      return a.milliseconds < b.milliseconds ? -1 : 1
    }

    if (a.microseconds !== b.microseconds) {
      return a.microseconds < b.microseconds ? -1 : 1
    }

    if (a.nanoseconds !== b.nanoseconds) {
      return a.nanoseconds < b.nanoseconds ? -1 : 1
    }

    return 0
  }

  static from (item) {
    if (!isObject(item)) {
      item = parseDurationString(toString(item))
    }

    if (Duration.isDuration(item)) {
      return new Duration(
        item.years,
        item.months,
        item.weeks,
        item.days,
        item.hours,
        item.minutes,
        item.seconds,
        item.milliseconds,
        item.microseconds,
        item.nanoseconds
      )
    }

    const result = {
      years: 0,
      months: 0,
      weeks: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
      microseconds: 0,
      nanoseconds: 0
    }
    let any = false

    const {
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = item

    if (years !== undefined) {
      any = true
      result.years = toIntegerWithoutRounding(years)
    }

    if (months !== undefined) {
      any = true
      result.months = toIntegerWithoutRounding(months)
    }

    if (weeks !== undefined) {
      any = true
      result.weeks = toIntegerWithoutRounding(weeks)
    }

    if (days !== undefined) {
      any = true
      result.days = toIntegerWithoutRounding(days)
    }

    if (hours !== undefined) {
      any = true
      result.hours = toIntegerWithoutRounding(hours)
    }

    if (minutes !== undefined) {
      any = true
      result.minutes = toIntegerWithoutRounding(minutes)
    }

    if (seconds !== undefined) {
      any = true
      result.seconds = toIntegerWithoutRounding(seconds)
    }

    if (milliseconds !== undefined) {
      any = true
      result.milliseconds = toIntegerWithoutRounding(milliseconds)
    }

    if (microseconds !== undefined) {
      any = true
      result.microseconds = toIntegerWithoutRounding(microseconds)
    }

    if (nanoseconds !== undefined) {
      any = true
      result.nanoseconds = toIntegerWithoutRounding(nanoseconds)
    }

    if (!any) {
      throw new TypeError('invalid duration-like')
    }

    return new Duration(
      result.years,
      result.months,
      result.weeks,
      result.days,
      result.hours,
      result.minutes,
      result.seconds,
      result.milliseconds,
      result.microseconds,
      result.nanoseconds
    )
  }

  #epochNano
  #years
  #months
  #weeks
  #days
  #hours
  #minutes
  #seconds
  #milliseconds
  #microseconds
  #nanoseconds
  #sign

  constructor (
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  ) {
    this.#years = years || 0
    this.#months = months || 0
    this.#weeks = weeks || 0
    this.#days = days || 0
    this.#hours = hours || 0
    this.#minutes = minutes || 0
    this.#seconds = seconds || 0
    this.#milliseconds = milliseconds || 0
    this.#microseconds = microseconds || 0
    this.#nanoseconds = nanoseconds || 0

    rejectDuration(
      this.years,
      this.months,
      this.weeks,
      this.days,
      this.hours,
      this.minutes,
      this.seconds,
      this.milliseconds,
      this.microseconds,
      this.nanoseconds
    )
  }

  get [SYMBOL_IS_DURATION] () {
    return true
  }

  get years () {
    return this.#years
  }

  get months () {
    return this.#months
  }

  get weeks () {
    return this.#weeks
  }

  get days () {
    return this.#days
  }

  get hours () {
    return this.#hours
  }

  get minutes () {
    return this.#minutes
  }

  get seconds () {
    return this.#seconds
  }

  get milliseconds () {
    return this.#milliseconds
  }

  get microseconds () {
    return this.#microseconds
  }

  get nanoseconds () {
    return this.#nanoseconds
  }

  get [Symbol.toStringTag] () {
    return 'DateTime.Duration'
  }

  get sign () {
    if (this.years !== 0) {
      return this.years < 0 ? -1 : 1
    }
    if (this.months !== 0) {
      return this.months < 0 ? -1 : 1
    }
    if (this.days !== 0) {
      return this.days < 0 ? -1 : 1
    }
    if (this.hours !== 0) {
      return this.hours < 0 ? -1 : 1
    }
    if (this.minutes !== 0) {
      return this.minutes < 0 ? -1 : 1
    }
    if (this.seconds !== 0) {
      return this.seconds < 0 ? -1 : 1
    }
    if (this.milliseconds !== 0) {
      return this.milliseconds < 0 ? -1 : 1
    }
    if (this.microseconds !== 0) {
      return this.microseconds < 0 ? -1 : 1
    }
    if (this.nanoseconds !== 0) {
      return this.nanoseconds < 0 ? -1 : 1
    }

    return 0
  }

  get blank () {
    return this.sign === 0
  }

  abs () {
    return new Duration(
      Math.abs(this.years),
      Math.abs(this.months),
      Math.abs(this.weeks),
      Math.abs(this.days),
      Math.abs(this.hours),
      Math.abs(this.minutes),
      Math.abs(this.seconds),
      Math.abs(this.milliseconds),
      Math.abs(this.microseconds),
      Math.abs(this.nanoseconds)
    )
  }

  add (durationLike, options) {
    let {
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = Duration.from(durationLike)
    const relativeTo =
      typeof options === 'object'
        ? DateTime.from(options.relativeTo)
        : DateTime.now()

    ;({
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = addDuration(
      this.years,
      this.months,
      this.weeks,
      this.days,
      this.hours,
      this.minutes,
      this.seconds,
      this.milliseconds,
      this.microseconds,
      this.nanoseconds,
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      relativeTo
    ))

    return new Duration(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    )
  }

  subtract (durationLike, options) {
    const sign = -1
    let {
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = Duration.from(durationLike)
    const relativeTo =
      typeof options === 'object'
        ? DateTime.from(options.relativeTo)
        : DateTime.now()

    ;({
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = addDuration(
      this.years,
      this.months,
      this.weeks,
      this.days,
      this.hours,
      this.minutes,
      this.seconds,
      this.milliseconds,
      this.microseconds,
      this.nanoseconds,
      sign * years,
      sign * months,
      sign * weeks,
      sign * days,
      sign * hours,
      sign * minutes,
      sign * seconds,
      sign * milliseconds,
      sign * microseconds,
      sign * nanoseconds,
      relativeTo
    ))

    return new Duration(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    )
  }

  negate () {
    return new Duration(
      -1 * this.years,
      -1 * this.months,
      -1 * this.weeks,
      -1 * this.days,
      -1 * this.hours,
      -1 * this.minutes,
      -1 * this.seconds,
      -1 * this.milliseconds,
      -1 * this.microseconds,
      -1 * this.nanoseconds
    )
  }

  round (optionsParam) {
    if (optionsParam === undefined) throw new TypeError('options parameter is required')
    let {
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = this

    let defaultLargestUnit = getDefaultLargestUnit(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    )
    const options =
      typeof optionsParam === 'string'
        ? { smallestUnit: optionsParam }
        : typeof optionsParam !== 'object'
          ? {}
          : optionsParam
    let smallestUnit = options.smallestUnit
    let smallestUnitPresent = true

    if (!smallestUnit) {
      smallestUnitPresent = false
      smallestUnit = 'nanosecond'
    }

    defaultLargestUnit = getLargestUnit(defaultLargestUnit, smallestUnit)
    let largestUnit = options.largestUnit
    let largestUnitPresent = true

    if (!largestUnit) {
      largestUnitPresent = false
      largestUnit = defaultLargestUnit
    }

    if (largestUnit === 'auto') largestUnit = defaultLargestUnit
    if (!smallestUnitPresent && !largestUnitPresent) {
      throw new RangeError(
        'at least one of smallestUnit or largestUnit is required'
      )
    }
    if (getLargestUnit(largestUnit, smallestUnit) !== largestUnit) {
      throw new RangeError(
        `largestUnit ${largestUnit} cannot be smaller than smallestUnit ${smallestUnit}`
      )
    }
    const roundingMode = options.roundingMode || 'halfExpand'
    const roundingIncrement = options.roundingIncrement || 1
    let relativeTo = DateTime.from(options.relativeTo)

    ;({ years, months, weeks, days } = unbalanceDurationRelative(
      years,
      months,
      weeks,
      days,
      largestUnit,
      relativeTo
    ))
    ;({
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = roundDuration(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      roundingIncrement,
      smallestUnit,
      roundingMode,
      relativeTo
    ))
    ;({
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = adjustRoundedDurationDays(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      roundingIncrement,
      smallestUnit,
      roundingMode,
      relativeTo
    ))
    ;({ years, months, weeks, days } = balanceDurationRelative(
      years,
      months,
      weeks,
      days,
      largestUnit,
      relativeTo
    ))
    if (relativeTo.isZonedDate) {
      relativeTo = moveRelativeZonedDateTime(
        relativeTo,
        years,
        months,
        weeks,
        0
      )
    }
    ;({
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = balanceDuration(
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      largestUnit,
      relativeTo
    ))

    return new Duration(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    )
  }

  mapUnits (callback) {
    const item = {}

    item.years = callback(this.years, 'years')
    item.months = callback(this.months, 'months')
    item.weeks = callback(this.weeks, 'weeks')
    item.days = callback(this.days, 'days')
    item.hours = callback(this.hours, 'hours')
    item.minutes = callback(this.minutes, 'minutes')
    item.seconds = callback(this.seconds, 'seconds')
    item.milliseconds = callback(this.milliseconds, 'milliseconds')
    item.microseconds = callback(this.microseconds, 'microseconds')
    item.nanoseconds = callback(this.nanoseconds, 'nanoseconds')

    return Duration.from(item)
  }

  forEachUnit (callback) {
    callback(this.years, 'years')
    callback(this.months, 'months')
    callback(this.weeks, 'weeks')
    callback(this.days, 'days')
    callback(this.hours, 'hours')
    callback(this.minutes, 'minutes')
    callback(this.seconds, 'seconds')
    callback(this.milliseconds, 'milliseconds')
    callback(this.microseconds, 'microseconds')
    callback(this.nanoseconds, 'nanoseconds')
  }

  equals (duration) {
    return Duration.compare(this, duration) === 0
  }

  toLocaleString (locale, options) {
    if (locale === undefined) {
      locale = Locale.systemLocale
    }

    return new DurationFormat(locale, options).format(this)
  }

  toString () {
    const dateParts = []
    if (this.years) dateParts.push(`${formatNumber(Math.abs(this.years))}Y`)
    if (this.months) dateParts.push(`${formatNumber(Math.abs(this.months))}M`)
    if (this.weeks) dateParts.push(`${formatNumber(Math.abs(this.weeks))}W`)
    if (this.days) dateParts.push(`${formatNumber(Math.abs(this.days))}D`)

    const timeParts = []
    if (this.hours) timeParts.push(`${formatNumber(Math.abs(this.hours))}H`)
    if (this.minutes) timeParts.push(`${formatNumber(Math.abs(this.minutes))}M`)

    const secondParts = []
    let total = totalDurationNanoseconds(
      0,
      0,
      0,
      this.seconds,
      this.milliseconds,
      this.microseconds,
      this.nanoseconds,
      0
    )
    let tmp = total / THOUSAND
    const nsBigInt = total % THOUSAND
    total = tmp
    tmp = total / THOUSAND
    const µsBigInt = total % THOUSAND
    total = tmp
    const secondsBigInt = total / THOUSAND
    const msBigInt = total % THOUSAND
    const fraction =
      Math.abs(Number(msBigInt)) * 1e6 +
      Math.abs(Number(µsBigInt)) * 1e3 +
      Math.abs(Number(nsBigInt))
    let decimalPart
    if (fraction !== 0) {
      decimalPart = `${fraction}`.padStart(9, '0')
      while (decimalPart[decimalPart.length - 1] === '0') {
        decimalPart = decimalPart.slice(0, -1)
      }
    }
    if (decimalPart) secondParts.unshift('.', decimalPart)
    if (secondsBigInt !== ZERO || secondParts.length) {
      secondParts.unshift(
        (secondsBigInt < ZERO
          ? secondsBigInt * NEGATIVE_ONE
          : secondsBigInt
        ).toString()
      )
    }
    if (secondParts.length) timeParts.push(`${secondParts.join('')}S`)
    if (timeParts.length) timeParts.unshift('T')
    if (!dateParts.length && !timeParts.length) return 'PT0S'
    return `${this.sign < 0 ? '-' : ''}P${dateParts.join('')}${timeParts.join(
      ''
    )}`
  }

  inspect () {
    return `Duration(${this.toString()})`
  }

  [Symbol.for('nodejs.util.inspect.custom')] () {
    return `Duration(\x1b[35m${this.toString()}\x1b[0m)`
  }

  toJSON () {
    return this.toString()
  }

  toBSON () {
    return this.toString()
  }

  with (durationLike) {
    let {
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = this

    if (typeof durationLike.years === 'number') {
      years = durationLike.years
    }

    if (typeof durationLike.months === 'number') {
      months = durationLike.months
    }

    if (typeof durationLike.weeks === 'number') {
      weeks = durationLike.weeks
    }

    if (typeof durationLike.days === 'number') {
      days = durationLike.days
    }

    if (typeof durationLike.hours === 'number') {
      hours = durationLike.hours
    }

    if (typeof durationLike.minutes === 'number') {
      minutes = durationLike.minutes
    }

    if (typeof durationLike.seconds === 'number') {
      seconds = durationLike.seconds
    }

    if (typeof durationLike.milliseconds === 'number') {
      milliseconds = durationLike.milliseconds
    }

    if (typeof durationLike.microseconds === 'number') {
      microseconds = durationLike.microseconds
    }

    if (typeof durationLike.nanoseconds === 'number') {
      nanoseconds = durationLike.nanoseconds
    }

    return new Duration(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    )
  }
}

export function durationSign (y, mon, w, d, h, min, s, ms, µs, ns) {
  for (const prop of [y, mon, w, d, h, min, s, ms, µs, ns]) {
    if (prop !== 0) return prop < 0 ? -1 : 1
  }

  return 0
}

function formatNumber (num) {
  if (num <= Number.MAX_SAFE_INTEGER) return num.toString(10)
  return BigInt(num).toString(10)
}

export function unbalanceDurationRelative (
  yearsParam,
  monthsParam,
  weeksParam,
  daysParam,
  largestUnit,
  relativeToParam
) {
  const sign = durationSign(
    yearsParam,
    monthsParam,
    weeksParam,
    daysParam,
    0,
    0,
    0,
    0,
    0,
    0
  )
  const signBI = BigInt(sign)

  let years = BigInt(yearsParam)
  let months = BigInt(monthsParam)
  let weeks = BigInt(weeksParam)
  let days = BigInt(daysParam)

  let calendar
  let relativeTo
  if (relativeToParam) {
    relativeTo = relativeToParam
    calendar = relativeTo.calendar
  }

  const oneYear = new Duration(sign)
  const oneMonth = new Duration(0, sign)
  const oneWeek = new Duration(0, 0, sign)

  switch (largestUnit) {
    case 'year':
      // no-op
      break
    case 'month': {
      if (!calendar) {
        throw new RangeError(
          'a starting point is required for months balancing'
        )
      }
      // assertExists(relativeTo);
      // balance years down to months
      while (!isZero(abs(years))) {
        const newRelativeTo = calendar.dateAdd(relativeTo, oneYear, undefined)
        const untilOptions = Object.create(null)
        untilOptions.largestUnit = 'month'
        const untilResult = calendar.dateUntil(
          relativeTo,
          newRelativeTo,
          untilOptions
        )
        const oneYearMonths = BigInt(untilResult.months)
        relativeTo = newRelativeTo
        months = months + oneYearMonths
        years = years - signBI
      }
      break
    }
    case 'week': {
      if (!calendar) throw new RangeError('a starting point is required for weeks balancing')
      // assertExists(relativeTo);
      const dateAdd = calendar.dateAdd
      // balance years down to days
      // balance years down to days
      while (!isZero(abs(years))) {
        let oneYearDays
        ;({ relativeTo, days: oneYearDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneYear,
          dateAdd
        ))
        days = days + BigInt(oneYearDays)
        years = years - signBI
      }

      // balance months down to days
      while (!isZero(abs(months))) {
        let oneMonthDays
        ;({ relativeTo, days: oneMonthDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneMonth,
          dateAdd
        ))
        days = days + BigInt(oneMonthDays)
        months = months - signBI
      }
      break
    }
    default: {
      // balance years down to days
      if (isZero(years) && isZero(months) && isZero(weeks)) break
      if (!calendar) throw new RangeError('a starting point is required for balancing calendar units')
      const dateAdd = calendar.dateAdd
      while (!isZero(abs(years))) {
        // assertExists(relativeTo);
        let oneYearDays
        ;({ relativeTo, days: oneYearDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneYear,
          dateAdd
        ))
        days = days + BigInt(oneYearDays)
        years = years - signBI
      }

      // balance months down to days
      while (!isZero(abs(months))) {
        // assertExists(relativeTo);
        let oneMonthDays
        ;({ relativeTo, days: oneMonthDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneMonth,
          dateAdd
        ))
        days = days + BigInt(oneMonthDays)
        months = months - signBI
      }

      // balance weeks down to days
      while (!isZero(abs(weeks))) {
        // assertExists(relativeTo);
        let oneWeekDays
        ;({ relativeTo, days: oneWeekDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneWeek,
          dateAdd
        ))
        days = days + BigInt(oneWeekDays)
        weeks = weeks - signBI
      }
      break
    }
  }

  return {
    years: Number(years),
    months: Number(months),
    weeks: Number(weeks),
    days: Number(days)
  }
}

export function balanceDurationRelative (
  yearsParam,
  monthsParam,
  weeksParam,
  daysParam,
  largestUnit,
  relativeToParam
) {
  const sign = durationSign(
    yearsParam,
    monthsParam,
    weeksParam,
    daysParam,
    0,
    0,
    0,
    0,
    0,
    0
  )

  if (sign === 0) {
    return {
      years: yearsParam,
      months: monthsParam,
      weeks: weeksParam,
      days: daysParam
    }
  }

  const signBI = BigInt(sign)

  let years = BigInt(yearsParam)
  let months = BigInt(monthsParam)
  let weeks = BigInt(weeksParam)
  let days = BigInt(daysParam)

  let calendar
  let relativeTo
  if (relativeToParam) {
    relativeTo = DateTime.from(relativeToParam)
    calendar = relativeTo.calendar
  }

  const oneYear = new Duration(sign)
  const oneMonth = new Duration(0, sign)
  const oneWeek = new Duration(0, 0, sign)

  switch (largestUnit) {
    case 'year': {
      if (!calendar) throw new RangeError('a starting point is required for years balancing')
      // assertExists(relativeTo);
      // balance days up to years
      let newRelativeTo, oneYearDays
      ;({ relativeTo: newRelativeTo, days: oneYearDays } = moveRelativeDate(
        calendar,
        relativeTo,
        oneYear
      ))
      while (abs(days) >= BigInt(Math.abs(oneYearDays))) {
        days = days - BigInt(oneYearDays)
        years = years + signBI
        relativeTo = newRelativeTo
        ;({ relativeTo: newRelativeTo, days: oneYearDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneYear
        ))
      }

      // balance days up to months
      let oneMonthDays
      ;({ relativeTo: newRelativeTo, days: oneMonthDays } = moveRelativeDate(
        calendar,
        relativeTo,
        oneMonth
      ))
      while (abs(days) >= BigInt(Math.abs(oneMonthDays))) {
        days = days - BigInt(oneMonthDays)
        months = months + signBI
        relativeTo = newRelativeTo
        ;({ relativeTo: newRelativeTo, days: oneMonthDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneMonth
        ))
      }

      // balance months up to years
      newRelativeTo = calendar.dateAdd(relativeTo, oneYear, undefined)
      const untilOptions = Object.create(null)
      untilOptions.largestUnit = 'month'
      let untilResult = calendar.dateUntil(
        relativeTo,
        newRelativeTo,
        untilOptions
      )
      let oneYearMonths = untilResult.months
      while (abs(months) >= BigInt(Math.abs(oneYearMonths))) {
        months = months - BigInt(oneYearMonths)
        years = years + signBI
        relativeTo = newRelativeTo
        newRelativeTo = calendar.dateAdd(relativeTo, oneYear, undefined)
        const untilOptions = Object.create(null)
        untilOptions.largestUnit = 'month'
        untilResult = calendar.dateUntil(
          relativeTo,
          newRelativeTo,
          untilOptions
        )
        oneYearMonths = untilResult.months
      }
      break
    }
    case 'month': {
      if (!calendar) throw new RangeError('a starting point is required for months balancing')
      // assertExists(relativeTo);
      // balance days up to months
      let newRelativeTo, oneMonthDays
      ;({ relativeTo: newRelativeTo, days: oneMonthDays } = moveRelativeDate(
        calendar,
        relativeTo,
        oneMonth
      ))
      while (abs(days) >= BigInt(Math.abs(oneMonthDays))) {
        days = days - BigInt(oneMonthDays)
        months = months + signBI
        relativeTo = newRelativeTo
        ;({ relativeTo: newRelativeTo, days: oneMonthDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneMonth
        ))
      }
      break
    }
    case 'week': {
      if (!calendar) throw new RangeError('a starting point is required for weeks balancing')
      // assertExists(relativeTo);
      // balance days up to weeks
      let newRelativeTo, oneWeekDays
      ;({ relativeTo: newRelativeTo, days: oneWeekDays } = moveRelativeDate(
        calendar,
        relativeTo,
        oneWeek
      ))
      while (abs(days) >= BigInt(Math.abs(oneWeekDays))) {
        days = days - BigInt(oneWeekDays)
        weeks = weeks + signBI
        relativeTo = newRelativeTo
        ;({ relativeTo: newRelativeTo, days: oneWeekDays } = moveRelativeDate(
          calendar,
          relativeTo,
          oneWeek
        ))
      }
      break
    }
    default:
      // no-op
      break
  }

  return {
    years: Number(years),
    months: Number(months),
    weeks: Number(weeks),
    days: Number(days)
  }
}

function daysUntil (earlier, later) {
  return differenceISODate(
    earlier.year,
    earlier.month,
    earlier.day,
    later.year,
    later.month,
    later.day,
    'day'
  ).days
}

function moveRelativeDate (calendar, relativeToParam, duration) {
  const later = calendar.dateAdd(relativeToParam, duration, undefined)
  const days = daysUntil(relativeToParam, later)
  return { relativeTo: later, days }
}

export function moveRelativeZonedDateTime (
  relativeTo,
  years,
  months,
  weeks,
  days
) {
  const timeZone = relativeTo.timeZone
  const calendar = relativeTo.calendar
  const intermediateNs = addZonedDateTime(
    relativeTo.epochNanoseconds,
    relativeTo.timeZone,
    relativeTo.calendar,
    years,
    months,
    weeks,
    days,
    0,
    0,
    0,
    0,
    0,
    0
  )

  return DateTime.from(intermediateNs, { timeZone, calendar })
}

function abs (x) {
  if (x < ZERO) return x * NEGATIVE_ONE
  return x
}

function isZero (input) {
  return input === ZERO
}
